import { useCallback, useState } from 'react'
import { socialApi } from '@/lib/socialApi'
import { MAX_ATTACHMENTS, attachmentLimit, formatSize, uploadFile } from '@/lib/upload'
import { NO_FILES, useDraftFilesStore, type PendingFile } from '@/store/draftFiles'
import type { AttachmentRef } from '@/types/api'

export type { PendingFile }

/**
 * Файлы к отправке: добавление из кнопки, перетаскивания и буфера, превью картинок,
 * загрузка по presigned-ссылкам с прогрессом перед отправкой сообщения.
 */
/**
 * Очередь вложений сообщения в канале; maxSize зависит от подписки автора и уровня сервера.
 * Файлы лежат в черновике канала (store/draftFiles): ушёл в другую беседу и вернулся — они на месте.
 */
export function useUploads(channelId: string, maxSize = attachmentLimit(false)) {
  const items = useDraftFilesStore((s) => s.files[channelId] ?? NO_FILES)
  const update = useDraftFilesStore((s) => s.update)
  const setItems = useCallback((fn: (prev: PendingFile[]) => PendingFile[]) => update(channelId, fn), [update, channelId])
  const [error, setError] = useState<string | null>(null)

  const add = useCallback((files: Iterable<File>) => {
    let problem: string | null = null
    setItems((prev) => {
      const next = [...prev]
      for (const file of files) {
        if (next.length >= MAX_ATTACHMENTS) {
          problem = `Не больше ${MAX_ATTACHMENTS} файлов в одном сообщении`
          break
        }
        if (file.size > maxSize) {
          problem = `«${file.name}» больше ${Math.round(maxSize / 1024 / 1024)} МБ — лимит поднимают «Кличат Плюс» и бусты сервера`
          continue
        }
        if (file.size === 0) {
          problem = `«${file.name}» пустой`
          continue
        }
        const preview = /^(image|video)\//.test(file.type) ? URL.createObjectURL(file) : null
        next.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file, preview, progress: 0, error: null })
      }
      return next
    })
    setError(problem)
  }, [maxSize, setItems])

  const remove = useCallback((id: string) => setItems((prev) => prev.filter((f) => f.id !== id)), [setItems])
  const clear = useCallback((ids?: string[]) => setItems((prev) => ids ? prev.filter((f) => !ids.includes(f.id)) : NO_FILES), [setItems])

  /** Загружает все файлы и возвращает ссылки для тела сообщения; при ошибке помечает файл. */
  const upload = useCallback(
    async (channelId: string): Promise<AttachmentRef[]> => {
      const current = items
      if (current.length === 0) return []
      const { attachments } = await socialApi.createUploads(
        channelId,
        current.map((f) => ({ id: f.id, filename: f.file.name, file_size: f.file.size, content_type: f.file.type || 'application/octet-stream' })),
      )
      const refs: AttachmentRef[] = []
      for (const f of current) {
        const slot = attachments.find((a) => a.id === f.id)
        if (!slot) throw new Error(`Нет ссылки для «${f.file.name}»`)
        try {
          await uploadFile(slot.upload_url, f.file, (p) => setItems((prev) => prev.map((x) => (x.id === f.id ? { ...x, progress: p } : x))))
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Не удалось загрузить'
          setItems((prev) => prev.map((x) => (x.id === f.id ? { ...x, error: msg } : x)))
          throw new Error(`${f.file.name} (${formatSize(f.file.size)}): ${msg}`)
        }
        refs.push({ id: f.id, filename: f.file.name, uploaded_filename: slot.upload_filename })
      }
      return refs
    },
    [items, setItems],
  )

  return { items, error, add, remove, clear, upload, setError }
}

/** Файлы из события вставки из буфера (скриншоты). */
export function filesFromPaste(e: React.ClipboardEvent): File[] {
  const out: File[] = []
  for (const item of e.clipboardData.items) {
    if (item.kind === 'file') {
      const f = item.getAsFile()
      if (f) out.push(f)
    }
  }
  return out
}
